import { cellKey } from '@blueprint/domain'
import { Draggable, Droppable, type DraggableProvided, type DroppableProvided } from '@hello-pangea/dnd'
import { useCallback, useRef, useState } from 'react'
import { CARD_SLOT_HEIGHT, CardPreview } from '../cards/CardPreview.js'
import { useBoardStore } from '../../state/board-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './VirtualCardList.module.css'
import { useBoardCardWindow } from './useBoardCardWindow.js'

export function parseCellKey(key: string): { rowId: string; columnId: string } {
  const [rowId = '', columnId = ''] = key.split('|')
  return { rowId, columnId }
}

interface VirtualCardListProps {
  rowId: string
  columnId: string
  cardIds: readonly string[]
  dragDisabled: boolean
  onOpen: (cardId: string) => void
  onMove: (cardId: string) => void
  onPrompt: (cardId: string) => void
}

/**
 * A virtual droppable, following the drag library's virtual-list pattern:
 * `mode="virtual"`, a rendered drag clone, and one extra virtual item so the
 * destination placeholder has space. Virtualization stays on during a drag — the cell
 * never mounts its whole card list.
 */
export function VirtualCardList({
  rowId,
  columnId,
  cardIds,
  dragDisabled,
  onOpen,
  onMove,
  onPrompt,
}: VirtualCardListProps) {
  return (
    <Droppable
      droppableId={cellKey(rowId, columnId)}
      mode="virtual"
      isDropDisabled={dragDisabled}
      renderClone={(provided, snapshot, rubric) => (
        <CloneCard
          cardId={cardIds[rubric.source.index] ?? ''}
          provided={provided}
          isDragging={snapshot.isDragging}
          onOpen={onOpen}
          onMove={onMove}
          onPrompt={onPrompt}
        />
      )}
    >
      {(provided, snapshot) => (
        <CellScroller
          droppableProvided={provided}
          // The extra slot reserves room for the placeholder while dragging over.
          itemCount={cardIds.length + (snapshot.isUsingPlaceholder ? 1 : 0)}
          cardIds={cardIds}
          dragDisabled={dragDisabled}
          onOpen={onOpen}
          onMove={onMove}
          onPrompt={onPrompt}
        />
      )}
    </Droppable>
  )
}

interface CellScrollerProps {
  droppableProvided: DroppableProvided
  itemCount: number
  cardIds: readonly string[]
  dragDisabled: boolean
  onOpen: (cardId: string) => void
  onMove: (cardId: string) => void
  onPrompt: (cardId: string) => void
}

function CellScroller({
  droppableProvided,
  itemCount,
  cardIds,
  dragDisabled,
  onOpen,
  onMove,
  onPrompt,
}: CellScrollerProps) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  // The slot is the card box plus its gap, and both scale with the board's zoom, so the
  // positions here must use the scaled height or they drift from the rendered layout.
  const slotHeight = CARD_SLOT_HEIGHT * useUiStore((state) => state.zoom)

  // The droppable's innerRef is recreated on each render of the child function, so hold
  // the latest in a ref and keep our own ref callback stable.
  const innerRef = useRef(droppableProvided.innerRef)
  innerRef.current = droppableProvided.innerRef

  const setRef = useCallback((element: HTMLDivElement | null) => {
    setScrollElement(element)
    innerRef.current(element)
  }, [])

  const { start, end } = useBoardCardWindow(scrollElement, itemCount, slotHeight)

  return (
    <div ref={setRef} className={styles.scroller} {...droppableProvided.droppableProps}>
      <div className={styles.sizer} style={{ height: `${itemCount * slotHeight}px` }}>
        {Array.from({ length: end - start }, (_, offset) => {
          const index = start + offset
          const cardId = cardIds[index]
          // The trailing virtual item is the placeholder slot: it occupies space only.
          if (cardId === undefined) return null
          return (
            <DraggableCard
              key={cardId}
              cardId={cardId}
              index={index}
              offset={index * slotHeight}
              slotHeight={slotHeight}
              dragDisabled={dragDisabled}
              onOpen={onOpen}
              onMove={onMove}
              onPrompt={onPrompt}
            />
          )
        })}
      </div>
      {itemCount === 0 ? <p className={styles.empty}>No cards</p> : null}
    </div>
  )
}

interface DraggableCardProps {
  cardId: string
  index: number
  offset: number
  slotHeight: number
  dragDisabled: boolean
  onOpen: (cardId: string) => void
  onMove: (cardId: string) => void
  onPrompt: (cardId: string) => void
}

function DraggableCard({ cardId, index, offset, slotHeight, dragDisabled, onOpen, onMove, onPrompt }: DraggableCardProps) {
  const card = useBoardStore((state) => state.entities?.cards[cardId])
  if (!card) return null
  return (
    <Draggable draggableId={cardId} index={index} isDragDisabled={dragDisabled}>
      {(provided, snapshot) => (
        <CardPreview
          card={card}
          provided={provided}
          isDragging={snapshot.isDragging}
          dragDisabled={dragDisabled}
          // `top` rather than a transform: the library owns `transform` while dragging,
          // and its resting style would otherwise blank ours out.
          style={{ position: 'absolute', top: offset, left: 0, width: '100%', height: slotHeight }}
          onOpen={onOpen}
          onMove={onMove}
          onPrompt={onPrompt}
        />
      )}
    </Draggable>
  )
}

interface CloneCardProps {
  cardId: string
  provided: DraggableProvided
  isDragging: boolean
  onOpen: (cardId: string) => void
  onMove: (cardId: string) => void
  onPrompt: (cardId: string) => void
}

function CloneCard({ cardId, provided, isDragging, onOpen, onMove, onPrompt }: CloneCardProps) {
  const card = useBoardStore((state) => state.entities?.cards[cardId])
  if (!card) return null
  return (
    <CardPreview
      card={card}
      provided={provided}
      isDragging={isDragging}
      dragDisabled={false}
      onOpen={onOpen}
      onMove={onMove}
      onPrompt={onPrompt}
    />
  )
}
